/* =============================================================================
 * FilterCheckboxGroup — shared filter primitive (KGH-795)
 * -----------------------------------------------------------------------------
 * The checkbox list inside a FilterSection. Extracted verbatim from the eight
 * copies in FilterSidebar.tsx. The <label> WRAPS its <input>, which is what
 * makes the whole row a hit target and needs no htmlFor — keep that structure.
 *
 * Two sizes, because the athlete side ships both: 'md' is the standard option
 * row, 'sm' is the denser row used inside a searchable list.
 * ========================================================================== */
import React from 'react';

export interface FilterOption {
  value: string;
  /** Defaults to `value`. */
  label?: React.ReactNode;
  /** Shown after the label, muted — e.g. how many athletes match. */
  count?: number;
  disabled?: boolean;
}

export interface FilterCheckboxGroupProps {
  /** Plain strings are accepted for the common "label === value" case. */
  options: Array<FilterOption | string>;
  selected: string[];
  onToggle: (value: string) => void;
  size?: 'md' | 'sm';
  /** Accessible name for the group as a whole. */
  ariaLabel?: string;
}

function normalise(o: FilterOption | string): FilterOption {
  return typeof o === 'string' ? { value: o } : o;
}

export const FilterCheckboxGroup: React.FC<FilterCheckboxGroupProps> = ({
  options,
  selected,
  onToggle,
  size = 'md',
  ariaLabel,
}) => {
  const sm = size === 'sm';
  return (
    <div role="group" aria-label={ariaLabel}>
      {options.map((raw) => {
        const opt = normalise(raw);
        const checked = selected.includes(opt.value);
        return (
          <label
            key={opt.value}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: sm ? '5px 0' : '6px 0',
              cursor: opt.disabled ? 'not-allowed' : 'pointer',
              fontSize: sm ? '13px' : '14px',
              color: 'var(--aps-control-label)',
              opacity: opt.disabled ? 0.5 : 1,
            }}
          >
            <input
              type="checkbox"
              checked={checked}
              disabled={opt.disabled}
              onChange={() => onToggle(opt.value)}
              style={{ width: '16px', height: '16px', cursor: opt.disabled ? 'not-allowed' : 'pointer', ...(sm ? { flexShrink: 0 } : null) }}
            />
            <span style={sm ? { lineHeight: '1.3' } : undefined}>{opt.label ?? opt.value}</span>
            {opt.count !== undefined && (
              <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--aps-control-muted)', fontVariantNumeric: 'tabular-nums' }}>
                {opt.count}
              </span>
            )}
          </label>
        );
      })}
    </div>
  );
};

export default FilterCheckboxGroup;
